const UTM_STORAGE_KEY = "c8_utm_params";
const FIRST_TOUCH_STORAGE_KEY = "c8_acquisition_first_touch";
const REFRESH_FLAG_KEY = "c8_acquisition_page_refreshed";

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;
const CLICK_ID_KEYS = ["gclid", "fbclid", "li_fat_id", "msclkid"] as const;

export type UtmParamKey = (typeof UTM_KEYS)[number];
export type ClickIdKey = (typeof CLICK_ID_KEYS)[number];
export type UtmParams = Partial<Record<UtmParamKey, string>>;
export type ClickIdParams = Partial<Record<ClickIdKey, string>>;

export type FirstTouchContext = {
  referrer_host?: string;
  landing_path?: string;
  in_app_browser?: boolean;
};

/** Detect common in-app browsers that often strip document.referrer. */
export function detectInAppBrowser(userAgent = ""): boolean {
  const ua = userAgent || (typeof navigator !== "undefined" ? navigator.userAgent : "") || "";
  return /FBAN|FBAV|Instagram|LinkedInApp|Line\/|Twitter|MicroMessenger|Snapchat|Pinterest|TikTok|BytedanceWebview|GSA\//i.test(
    ua,
  );
}

function parseSearchParams(search: string): URLSearchParams {
  const raw = search.startsWith("?") ? search.slice(1) : search;
  return new URLSearchParams(raw);
}

function parseUtmFromSearch(search: string): UtmParams {
  const params = parseSearchParams(search);
  const out: UtmParams = {};
  for (const key of UTM_KEYS) {
    const value = params.get(key)?.trim();
    if (value) out[key] = value;
  }
  return out;
}

function parseClickIdsFromSearch(search: string): ClickIdParams {
  const params = parseSearchParams(search);
  const out: ClickIdParams = {};
  for (const key of CLICK_ID_KEYS) {
    const value = params.get(key)?.trim();
    if (value) out[key] = value;
  }
  return out;
}

function hasAnyUtm(params: UtmParams): boolean {
  return UTM_KEYS.some((key) => Boolean(params[key]));
}

function hasAnyClickId(params: ClickIdParams): boolean {
  return CLICK_ID_KEYS.some((key) => Boolean(params[key]));
}

function readStoredAcquisitionBlob(): Record<string, string> {
  if (typeof window === "undefined") return {};
  try {
    const raw = sessionStorage.getItem(UTM_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const out: Record<string, string> = {};
    for (const [key, value] of Object.entries(parsed || {})) {
      if (value != null && String(value).trim() !== "") {
        out[key] = String(value).trim();
      }
    }
    return out;
  } catch {
    return {};
  }
}

function writeStoredAcquisitionBlob(next: Record<string, string>): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
}

/** Persist UTM + click-id params for the tab session when present in the URL. */
export function captureUtmFromSearch(search: string): void {
  if (typeof window === "undefined") return;
  const incomingUtm = parseUtmFromSearch(search);
  const incomingClick = parseClickIdsFromSearch(search);
  if (!hasAnyUtm(incomingUtm) && !hasAnyClickId(incomingClick)) return;

  const existing = readStoredAcquisitionBlob();
  // First-touch: keep existing keys, fill blanks from the new URL.
  const next = { ...existing };
  for (const key of UTM_KEYS) {
    if (!next[key] && incomingUtm[key]) next[key] = incomingUtm[key]!;
  }
  for (const key of CLICK_ID_KEYS) {
    if (!next[key] && incomingClick[key]) next[key] = incomingClick[key]!;
  }
  writeStoredAcquisitionBlob(next);
}

function decodeBackParam(back: string): string | null {
  let decoded = back.trim();
  if (!decoded) return null;
  try {
    decoded = decodeURIComponent(decoded);
  } catch {
    /* use raw */
  }
  return decoded;
}

function hostFromReferrer(referrer: string): string | null {
  const raw = String(referrer || "").trim();
  if (!raw) return null;
  try {
    const url = new URL(raw);
    if (typeof window !== "undefined" && url.origin === window.location.origin) {
      return null;
    }
    const host = url.hostname.toLowerCase().replace(/\.$/, "");
    if (!host || host === "localhost" || host.endsWith(".local")) return null;
    return host;
  } catch {
    return null;
  }
}

function readFirstTouch(): FirstTouchContext {
  if (typeof window === "undefined") return {};
  try {
    const raw = sessionStorage.getItem(FIRST_TOUCH_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const out: FirstTouchContext = {};
    if (parsed?.referrer_host != null && String(parsed.referrer_host).trim()) {
      out.referrer_host = String(parsed.referrer_host).trim();
    }
    if (parsed?.landing_path != null && String(parsed.landing_path).trim()) {
      out.landing_path = String(parsed.landing_path).trim();
    }
    if (parsed?.in_app_browser === true || parsed?.in_app_browser === false) {
      out.in_app_browser = Boolean(parsed.in_app_browser);
    }
    return out;
  } catch {
    return {};
  }
}

function writeFirstTouch(next: FirstTouchContext): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(FIRST_TOUCH_STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
}

/**
 * Capture first landing path + external referrer host once per tab session.
 * Later navigations (e.g. /signup) must not overwrite the original touch.
 */
export function captureFirstTouchContext({
  pathname,
  referrer,
}: {
  pathname: string;
  referrer?: string;
}): void {
  if (typeof window === "undefined") return;
  const existing = readFirstTouch();
  const next: FirstTouchContext = { ...existing };

  if (!next.landing_path) {
    const path = String(pathname || "").trim() || "/";
    next.landing_path = path.startsWith("/") ? path : `/${path}`;
  }

  if (!next.referrer_host) {
    const host = hostFromReferrer(referrer ?? (typeof document !== "undefined" ? document.referrer : ""));
    if (host) next.referrer_host = host;
  }

  if (next.in_app_browser === undefined) {
    next.in_app_browser = detectInAppBrowser();
  }

  noteDocumentReload();

  if (
    next.landing_path !== existing.landing_path ||
    next.referrer_host !== existing.referrer_host ||
    next.in_app_browser !== existing.in_app_browser
  ) {
    writeFirstTouch(next);
  }
}

/** Capture UTMs/click ids from the current route and from `back=` on login/signup. */
export function tryCaptureUtmFromRouter({
  pathname,
  search,
}: {
  pathname: string;
  search: string;
}): void {
  captureUtmFromSearch(search);
  captureFirstTouchContext({
    pathname,
    referrer: typeof document !== "undefined" ? document.referrer : "",
  });
  const back = parseSearchParams(search).get("back");
  if (!back) return;
  const decoded = decodeBackParam(back);
  if (!decoded) return;
  const qIndex = decoded.indexOf("?");
  if (qIndex === -1) return;
  captureUtmFromSearch(decoded.slice(qIndex));
}

export function getSessionUtmParams(): UtmParams {
  const blob = readStoredAcquisitionBlob();
  const out: UtmParams = {};
  for (const key of UTM_KEYS) {
    if (blob[key]) out[key] = blob[key];
  }
  return out;
}

export function getSessionClickIdParams(): ClickIdParams {
  const blob = readStoredAcquisitionBlob();
  const out: ClickIdParams = {};
  for (const key of CLICK_ID_KEYS) {
    if (blob[key]) out[key] = blob[key];
  }
  return out;
}

export function getSessionFirstTouchContext(): FirstTouchContext {
  return readFirstTouch();
}

/** Remember a full reload for this tab. Sign up links do not count — only a browser refresh. */
export function noteDocumentReload(): void {
  if (typeof window === "undefined") return;
  try {
    const nav = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
    if (nav?.type === "reload") {
      sessionStorage.setItem(REFRESH_FLAG_KEY, "1");
    }
  } catch {
    /* ignore */
  }
}

export function wasDocumentReloaded(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return sessionStorage.getItem(REFRESH_FLAG_KEY) === "1";
  } catch {
    return false;
  }
}

/** All five UTM keys for acquisition events (`null` when not captured). */
export function getSessionUtmPayloadWithNulls(): Record<UtmParamKey, string | null> {
  const utm = getSessionUtmParams();
  return {
    utm_source: utm.utm_source ?? null,
    utm_medium: utm.utm_medium ?? null,
    utm_campaign: utm.utm_campaign ?? null,
    utm_content: utm.utm_content ?? null,
    utm_term: utm.utm_term ?? null,
  };
}

export function getSessionClickIdPayloadWithNulls(): Record<ClickIdKey, string | null> {
  const ids = getSessionClickIdParams();
  return {
    gclid: ids.gclid ?? null,
    fbclid: ids.fbclid ?? null,
    li_fat_id: ids.li_fat_id ?? null,
    msclkid: ids.msclkid ?? null,
  };
}
